import React, { Component } from 'react'
import { Button, Alert, Badge, UncontrolledTooltip } from 'reactstrap'
import { graphql } from '@apollo/client/react/hoc'
import gql from 'graphql-tag'
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import withApolloClient from '../../withApolloClient'
import toTitleCase from '../../Functions/toTitleCase'
import AddPeopleModal from '../IncidentModals/AddPeopleModal'
import ChangeDriverModal from '../IncidentModals/ChangeDriverModal'
import PersonInfo from './PersonInfo'
import AddVehicleModal from '../IncidentModals/AddVehicleModal'
import AddPartyEntityModal from '../IncidentModals/AddPartyEntityModal'
import VehicleInfo from './VehicleInfo'
import DatetimeRenderer from '../../Material/DatetimeRenderer'
import HistoryList from '../../Material/HistoryList'
import ConfirmationPopup from '../../Material/ConfirmationPopup'
import ScopedDocumentListContainer from '../DocumentsTab/ScopedDocumentListContainer'
import SeatArrangement from './SeatArrangement'
import Select from 'react-select'
import './PartiesTab.css'
import getFormattedMetadata from "../../Functions/getFormattedMetadata"

const AllIncidentParties = gql`query AllIncidentParties($incidentId: ID!, $isArchived: Boolean) {
    allIncidentParties(incidentId:$incidentId, isArchived:$isArchived) {
        edges {
            node {
                id
                pk
                dateAdded
                partyType
                partyName
                isDefault
                metadata
                documentSlug
                isArchived
                incident{
                    id
                    canAddDriver
                    canEditDriver
                    branch{
                        id
                        customId
                    }
                    driver{
                        id
                    }
                    rental {
                        id
                    }
                }
                vehicles {
                    edges {
                        node {
                            id
                            dateAdded
                            vin
                            make
                            model
                            year
                            plateNumber
                            damageDescription
                            insuranceCompany
                            policyNumber
                            belongsToFleet
                        }
                    }
                }
                incidentpartypeopleSet{
                    edges {
                        node {
                            id
                            pk
                            dateAdded
                            isArchived
                            name
                            phoneNumber
                            email
                            address
                            role
                            driverLicense
                            injuries
                            statement
                            isBusinessEntity
                            respresentative {
                                id
                                name
                            }
                        }
                    }
                }
            }
        }
    }
}`

const ArchiveIncidentParty = gql`
    mutation ArchiveIncidentPartyMutation($input: ArchiveIncidentPartyMutationInput!) {
        archiveIncidentParty(input: $input) {
            ok
            errors {
                field
                messages
            }
        }
    }
`
const UnarchiveIncidentParty = gql`
    mutation UnarchiveIncidentPartyMutation($input: UnarchiveIncidentPartyMutationInput!) {
        unarchiveIncidentParty(input: $input) {
            ok
            errors {
                field
                messages
            }
        }
    }
`
const UpdateIncidentPartyPeople = gql`
    mutation updateIncidentPartyPeopleMutation($input: UpdateIncidentPartyPeopleMutationInput!){
    updateIncidentPartyPeople(input:$input){
        ok
        errors{
            field
            messages
        }
    }
} `
const UpdateIncidentParty = gql`
    mutation updateIncidentPartyMutation($input: UpdateIncidentPartyMutationInput!) {
        updateIncidentParty(input: $input) {
            ok
            errors {
                field
                messages
            }
        }
    }
`
const SyncIncidentDriverInfo = gql`
    mutation SyncIncidentDriverInfo($input: SyncIncidentDriverInfoMutationInput!) {
        syncIncidentDriverInfo(input: $input) {
            ok
            errors {
                field
                messages
            }
        }
    }
`

const DRIVER_ROLES = ["VEHICLE_DRIVER", "UNAUTHORIZED_DRIVER", "EMPLOYEE", "VENDOR"]
const OWNER_ROLE = "VEHICLE_OWNER"

class PartiesTab extends Component {
    constructor(props) {
        super(props)
        this.state = {
            openPartyId: null,
            didSetInitialOpen: false,
            hasLoadedParties: false,
            openModal: '',
            loading: false,
            error: '',
            editingPartyId: null,
            partyNameInput: '',
            partyNameSaving: false,
            syncingDriverInfo: false,
        }
    }

    componentDidUpdate(prevProps) {
        if (!this.state.hasLoadedParties && this.props.allIncidentParties) {
            this.setState({ hasLoadedParties: true })
        }
        if (this.state.didSetInitialOpen) return
        const prevParties = prevProps.allIncidentParties?.edges || []
        const nextParties = this.props.allIncidentParties?.edges || []
        if (prevParties.length === 0 && nextParties.length > 0) {
            const nodes = nextParties.map(edge => edge.node)
            const vehicleParty = nodes.find(party => party.partyType === 'VEHICLE')
            const firstParty = vehicleParty || nodes[0]
            this.setState({ openPartyId: firstParty?.id || null, didSetInitialOpen: true })
        }
    }

    handlePartyArchive = (id) => {
        this.setState({ loading: true, error: '' })
        const mutation = this.props.isArchived ? UnarchiveIncidentParty : ArchiveIncidentParty
        this.props.apolloClient.mutate({
            mutation,
            variables: { input: { partyId: id } },
        }).then((result) => {
            const dataKey = this.props.isArchived ? 'unarchiveIncidentParty' : 'archiveIncidentParty'
            const res = result.data && result.data[dataKey]
            if (res && res.ok) {
                this.props.refetchQuery && this.props.refetchQuery()
                this.setState({ openModal: "" })
            } else if (res?.errors) {
                this.setState({ loading: false, error: res.errors[0].messages[0] })
            }
        }).catch((error) => {
            let errorMessage = "An error has occured" + (error.message ? (": " + error.message) : ".")
            this.setState({ error: errorMessage, loading: false, })
        }).finally(() => {
            this.setState({ loading: false })
        })
    }

    handlePeopleArchive = (id, isArchived) => {
        this.setState({ loading: true, error: '' })
        const mutation = UpdateIncidentPartyPeople
        this.props.apolloClient.mutate({
            mutation,
            variables: { input: { partyPeopleId: id, isArchived: !isArchived } },
        }).then((result) => {
            let res = result.data && result.data["updateIncidentPartyPeople"]
            if (res && res.ok) {
                this.props.refetchQuery && this.props.refetchQuery()
                this.setState({ openModal: "" })
            } else if (res?.errors) {
                this.setState({ loading: false, error: res.errors[0].messages[0] })
            }
        }).catch((error) => {
            let errorMessage = "An error has occured" + (error.message ? (": " + error.message) : ".")
            this.setState({ error: errorMessage, loading: false, })
        }).finally(() => {
            this.setState({ loading: false })
        })
    }

    syncIncidentDriverInfo = (partyId) => {
        this.setState({ syncingDriverInfo: true, error: '' })
        this.props.apolloClient.mutate({
            mutation: SyncIncidentDriverInfo,
            variables: { input: { partyId } },
        }).then((result) => {
            const response = result.data && result.data["syncIncidentDriverInfo"]
            if (response && response.ok) {
                this.props.refetchQuery && this.props.refetchQuery()
                this.setState({ openModal: '' })
            } else if (response?.errors) {
                this.setState({ error: response.errors[0].messages[0] })
            }
        }).catch((error) => {
            const errorMessage = "An error has occured" + (error.message ? (": " + error.message) : ".")
            this.setState({ error: errorMessage })
        }).finally(() => {
            this.setState({ syncingDriverInfo: false })
        })
    }

    handlePassengerPositionChange = (personId, passengerPosition) => {
        this.setState({ loading: true, error: '' })
        this.props.apolloClient.mutate({
            mutation: UpdateIncidentPartyPeople,
            variables: { input: { partyPeopleId: personId, passengerPosition } },
        }).then((result) => {
            let res = result.data && result.data["updateIncidentPartyPeople"]
            if (res && res.ok) {
                this.props.refetchQuery && this.props.refetchQuery()
            } else if (res?.errors) {
                this.setState({ error: res.errors[0].messages[0] })
            }
        }).catch((error) => {
            let errorMessage = "An error has occured" + (error.message ? (": " + error.message) : ".")
            this.setState({ error: errorMessage })
        }).finally(() => {
            this.setState({ loading: false })
        })
    }

    startPartyNameEdit = (party) => {
        this.setState({
            editingPartyId: party.id,
            partyNameInput: party.partyName || '',
            error: '',
        })
    }

    cancelPartyNameEdit = () => {
        this.setState({
            editingPartyId: null,
            partyNameInput: '',
            partyNameSaving: false,
            error: '',
        })
    }

    savePartyName = () => {
        const { editingPartyId, partyNameInput } = this.state
        const trimmedName = partyNameInput?.trim()
        if (!editingPartyId) return
        if (!trimmedName) {
            this.setState({ error: 'Party name is required.' })
            return
        }

        this.setState({ partyNameSaving: true, error: '' })
        this.props.apolloClient.mutate({
            mutation: UpdateIncidentParty,
            variables: { input: { partyId: editingPartyId, partyName: trimmedName } },
        }).then((result) => {
            const res = result?.data?.updateIncidentParty
            if (res?.ok) {
                this.props.refetchQuery && this.props.refetchQuery()
                this.cancelPartyNameEdit()
            } else if (res?.errors) {
                this.setState({ error: res.errors[0].messages[0] })
            }
        }).catch((error) => {
            const errorMessage = "An error has occured" + (error.message ? (": " + error.message) : ".")
            this.setState({ error: errorMessage })
        }).finally(() => {
            this.setState({ partyNameSaving: false })
        })
    }

    renderArchivePartyModal = (partyType, partyId) => {
        const { isArchived } = this.props
        return (
            <ConfirmationPopup
                open={true}
                message={`Are you sure you want to ${isArchived ? "un-archive" : "archive"} this ${toTitleCase(partyType)} party?`}
                confirmAction={() => this.handlePartyArchive(partyId)}
                action={isArchived ? "Un-Archive" : "Yes"}
                color={isArchived ? "success" : "danger"}
                loading={this.state.loading}
                handleClose={() => this.setState({ openModal: "" })}
                error={this.state.error ? this.state.error : ""}
            />
        )
    }

    renderArchivePartyPeopleModal = (personRole, personId, isArchived) => {
        return (
            <ConfirmationPopup
                open={true}
                message={`Are you sure you want to ${isArchived ? "un-archive" : "archive"} this ${toTitleCase(personRole)}?`}
                confirmAction={() => this.handlePeopleArchive(personId, isArchived)}
                action={isArchived ? "Un-Archive" : "Yes"}
                color={isArchived ? "success" : "danger"}
                loading={this.state.loading}
                handleClose={() => this.setState({ openModal: "" })}
                error={this.state.error ? this.state.error : ""}
            />
        )
    }

    renderVehiclePartyInfo = (party) => {
        const hasRental = Boolean(party?.incident?.rental?.id)
        const defaultCustomerId = party?.incident?.driver?.id
        const hasIncidentDriver = Boolean(defaultCustomerId)
        const showArchivedPeople = Boolean(this.state[`showArchivedPeople_${party.id}`])
        const vehicles = party?.vehicles?.edges || []
        const allPeople = party?.incidentpartypeopleSet?.edges || []
        const visiblePeople = allPeople.filter(person => Boolean(person?.node?.isArchived) === showArchivedPeople)
        const driverPeople = visiblePeople.filter(person => DRIVER_ROLES.includes(person.node.role))
        const ownerPeople = visiblePeople.filter(person => person.node.role === OWNER_ROLE)
        const passengerPeople = visiblePeople.filter(person => person.node.role === 'VEHICLE_PASSENGER')
        const hasVehicleDriver = allPeople.some(person => person.node.role === 'VEHICLE_DRIVER' && !person.node.isArchived)
        const passengerPositionMap = (() => {
            if (!party?.metadata) return {}
            if (typeof party.metadata === 'string') {
                try {
                    const parsed = JSON.parse(party.metadata)
                    return parsed?.passenger_positions || {}
                } catch {
                    return {}
                }
            }
            return party.metadata.passenger_positions || {}
        })()
        const passengerPositionById = Object.entries(passengerPositionMap).reduce((acc, [passengerId, seatLabel]) => {
            acc[String(passengerId)] = seatLabel
            return acc
        }, {})

        let formattedMetadata = getFormattedMetadata(party.metadata)
        let hasMetadata = formattedMetadata && formattedMetadata !== '{}' && formattedMetadata !== '[]'

        return (
            <div className="parties-detail">
                <div className="parties-section">
                    {hasMetadata && <>
                        <div className="parties-section__title parties-section__title--blue">Metadata</div>
                        <div className="parties-section__content">
                            <pre className="bg-light border rounded p-3 mb-0" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {formattedMetadata}
                            </pre>
                        </div>
                    </>}

                    <div className="parties-section__title parties-section__title--blue">Vehicle Information</div>
                    <div className="parties-section__content">
                        {vehicles?.map((veh) => (
                            <div key={veh.node.id} className="parties-card parties-card--plain">
                                <VehicleInfo vehicle={veh.node} refetchQuery={this.props.refetchQuery} isDefault={party.isDefault} />
                            </div>
                        ))}
                        {vehicles?.length === 0 && (
                            <div className="parties-empty-row">
                                <div className="parties-empty-row__text">No vehicles added. Click "Add Vehicle" to add one.</div>
                                {!party.isArchived && !showArchivedPeople &&
                                    <Button color="primary" outline size="sm" className="parties-action parties-action--blue" onClick={() => this.setState({ openModal: 'AddVehicleEntityModal' })}>
                                        <i className="fa fa-plus" /> Add Vehicle
                                    </Button>
                                }
                            </div>
                        )}
                        {this.state.openModal === 'AddVehicleEntityModal' && (
                            <AddPartyEntityModal
                                isOpen={true}
                                toggle={() => this.setState({ openModal: '' })}
                                entity="VEHICLE"
                                partyId={party?.id}
                                refetchQuery={this.props.refetchQuery}
                            />
                        )}
                    </div>
                </div>

                <div className="parties-section">
                    <div className="parties-section__header">
                        <div className="parties-section__title parties-section__title--green">Driver Information</div>
                        {party.isDefault ? <>
                            {!party.isArchived && !showArchivedPeople &&
                                <Button color="success" outline size="sm" className="parties-action" onClick={() => this.setState({ openModal: `AddDriverModal_${party.pk}` })}>
                                    <i className="fa fa-plus" /> Add Driver
                                </Button>
                            }
                            {this.state.openModal === `AddDriverModal_${party.pk}` && (
                                <AddPeopleModal
                                    isOpen={true}
                                    toggle={() => this.setState({ openModal: '' })}
                                    partyType="VEHICLE"
                                    roles={!hasVehicleDriver && party.incident.canAddDriver ? DRIVER_ROLES : DRIVER_ROLES.filter(item => item !== 'VEHICLE_DRIVER')}
                                    partyId={party.id}
                                    incidentId={party?.incident?.id}
                                    isDefaultParty={party.isDefault}
                                    incidentBranchIds={party?.incident?.branch?.id ? [party.incident.branch.id] : null}
                                    refetchQuery={this.props.refetchQuery}
                                />
                            )}
                        </> :
                            allPeople.filter(person => ["VEHICLE_DRIVER", 'UNAUTHORIZED_DRIVER'].includes(person.node.role) && !person.node.isArchived).length === 0 &&
                            <>
                                {!party.isArchived && !showArchivedPeople &&
                                    <Button color="success" outline size="sm" className="parties-action" onClick={() => this.setState({ openModal: `AddDriverModal_${party.pk}` })}>
                                        <i className="fa fa-plus" /> Add Driver
                                    </Button>
                                }
                                {this.state.openModal === `AddDriverModal_${party.pk}` && (
                                    <AddPeopleModal
                                        isOpen={true}
                                        toggle={() => this.setState({ openModal: '' })}
                                        partyType="VEHICLE"
                                        role="VEHICLE_DRIVER"
                                        partyId={party.id}
                                        refetchQuery={this.props.refetchQuery}
                                    />
                                )}
                            </>
                        }
                    </div>
                    <div className="parties-section__content">
                        {driverPeople.map((person, idx) => (
                            <div key={person.node.id} className="parties-card parties-card--green">
                                <div className="parties-card__header">
                                    <div className="parties-card__title">Driver {idx + 1}</div>
                                    {!party.isArchived &&
                                        <div className="parties-card__actions">
                                            {party.isDefault && person.node.role === 'VEHICLE_DRIVER' && !person.node.isArchived && !this.props.isArchived && (
                                                <>
                                                    <button type="button" className="parties-archive" title="Sync driver info" onClick={() => this.setState({ openModal: `syncDriverInfo_${person.node.pk}`, error: '' })}>
                                                        <i className={`fa fa-refresh${this.state.syncingDriverInfo ? ' fa-spin' : ''}`} />
                                                    </button>
                                                    {this.state.openModal === `syncDriverInfo_${person.node.pk}` && (
                                                        <ConfirmationPopup
                                                            open={true}
                                                            message="Are you sure you want to sync this driver info?"
                                                            confirmAction={() => this.syncIncidentDriverInfo(party.id)}
                                                            action="Yes, Sync"
                                                            color="success"
                                                            loading={this.state.syncingDriverInfo}
                                                            handleClose={() => this.setState({ openModal: '', error: '' })}
                                                            error={this.state.error || ''}
                                                        />
                                                    )}
                                                </>
                                            )}
                                            {(!party.isDefault || (person.node.role === 'VEHICLE_DRIVER' && party.incident.canEditDriver)) &&
                                                <button type='button' className='parties-archive' onClick={() => this.setState({ openModal: `ArchiveDriver_${person.node.pk}` })}>
                                                    <i className={`fa ${person.node.isArchived ? "fa-undo" : "fa-archive"}`} />
                                                </button>
                                            }
                                            {party.incident.canEditDriver && party.isDefault && (
                                                <button type='button' id={`editDriverTooltip_${person.node.pk}`} className='parties-archive' onClick={() => this.setState({ openModal: `EditDriverModal_${person.node.pk}` })}>
                                                    <i className={"fa fa-edit"} />
                                                    <UncontrolledTooltip target={`editDriverTooltip_${person.node.pk}`} placement="top">Change Driver</UncontrolledTooltip>
                                                </button>
                                            )}
                                        </div>
                                    }
                                    {this.state.openModal === `ArchiveDriver_${person.node.pk}` && this.renderArchivePartyPeopleModal(person.node.role, person.node.id, person.node.isArchived)}
                                    {this.state.openModal === `EditDriverModal_${person.node.pk}` && (
                                        <ChangeDriverModal
                                            isOpen={true}
                                            toggle={() => this.setState({ openModal: '' })}
                                            incidentId={party?.incident?.id}
                                            partyPeople={person.node}
                                            incidentBranchIds={party?.incident?.branch?.id ? [party.incident.branch.id] : null}
                                            refetchQuery={this.props.refetchQuery}
                                        />
                                    )}
                                </div>
                                <PersonInfo person={person.node} refetchQuery={this.props.refetchQuery} isDefault={party.isDefault} hasRental={hasRental} defaultCustomerId={defaultCustomerId} isReadOnly={showArchivedPeople || party.isArchived} hasDriver={hasIncidentDriver} branchCustomId={party?.incident?.branch?.customId} />
                            </div>
                        ))}
                        {driverPeople.length === 0 && (
                            <div className="parties-empty-row">
                                <div className="parties-empty-row__text">No drivers added. Click "Add Driver" to add one.</div>
                            </div>
                        )}
                    </div>
                </div>

                {!party.isDefault && (
                    <div className="parties-section">
                        <div className="parties-section__header">
                            <div className="parties-section__title parties-section__title--orange">Owner Information</div>
                            {ownerPeople.length === 0 && !party.isArchived && !showArchivedPeople &&
                                <Button color="warning" outline size="sm" className="parties-action" onClick={() => this.setState({ openModal: `AddOwnerModal_${party.pk}` })}>
                                    <i className="fa fa-plus" /> Add Owner
                                </Button>
                            }
                        </div>
                        <div className="parties-section__content">
                            {ownerPeople.map((person, idx) => (
                                <div key={person.node.id} className="parties-card parties-card--orange">
                                    <div className="parties-card__header">
                                        <div className="parties-card__title">Owner {idx + 1}</div>
                                        {!party.isArchived &&
                                            <button type='button' className='parties-archive' onClick={() => this.setState({ openModal: `ArchiveOwner_${person.node.pk}` })}>
                                                <i className={`fa ${person.node.isArchived ? "fa-undo" : "fa-archive"}`} />
                                            </button>
                                        }
                                        {this.state.openModal === `ArchiveOwner_${person.node.pk}` && this.renderArchivePartyPeopleModal(person.node.role, person.node.id, person.node.isArchived)}
                                    </div>
                                    <PersonInfo person={person.node} refetchQuery={this.props.refetchQuery} isDefault={party.isDefault} hasRental={hasRental} defaultCustomerId={defaultCustomerId} isReadOnly={showArchivedPeople || party.isArchived} hasDriver={hasIncidentDriver} branchCustomId={party?.incident?.branch?.customId} />
                                </div>
                            ))}
                            {ownerPeople.length === 0 && (
                                <div className="parties-empty-row">
                                    <div className="parties-empty-row__text">No owners added. Click "Add Owner" to add one.</div>
                                </div>
                            )}
                            {this.state.openModal === `AddOwnerModal_${party.pk}` && (
                                <AddPeopleModal
                                    isOpen={true}
                                    toggle={() => this.setState({ openModal: '' })}
                                    partyType="VEHICLE"
                                    role={OWNER_ROLE}
                                    partyId={party.id}
                                    refetchQuery={this.props.refetchQuery}
                                />
                            )}
                        </div>
                    </div>
                )}

                <div className="parties-section">
                    <div className="parties-section__header">
                        <div className="parties-section__title parties-section__title--purple">Passenger Information</div>
                        {!party.isArchived && !showArchivedPeople &&
                            <Button color="secondary" outline size="sm" className="parties-action parties-action--purple" onClick={() => this.setState({ openModal: `AddPassengerModal_${party.pk}` })}>
                                <i className="fa fa-plus" /> Add Passenger
                            </Button>
                        }
                    </div>
                    <div className="parties-section__content">
                        {passengerPeople.map((person, idx) => (
                            <div key={person.node.id} className="parties-card parties-card--purple">
                                <div className="parties-card__header">
                                    <div className="parties-card__title">Passenger {idx + 1}</div>
                                    {!party.isArchived &&
                                        <button type='button' className='parties-archive' onClick={() => this.setState({ openModal: `ArchivePassenger_${person.node.pk}` })}>
                                            <i className={`fa ${person.node.isArchived ? "fa-undo" : "fa-archive"}`} />
                                        </button>
                                    }
                                    {this.state.openModal === `ArchivePassenger_${person.node.pk}` && this.renderArchivePartyPeopleModal(person.node.role, person.node.id, person.node.isArchived)}
                                </div>
                                <PersonInfo person={person.node} refetchQuery={this.props.refetchQuery} isDefault={party.isDefault} hasRental={hasRental} defaultCustomerId={defaultCustomerId} passengerPosition={passengerPositionById[String(person.node.pk || person.node.id)]} isReadOnly={showArchivedPeople || party.isArchived} hasDriver={hasIncidentDriver} branchCustomId={party?.incident?.branch?.customId} />
                            </div>
                        ))}
                        {passengerPeople.length === 0 && (
                            <div className="parties-empty-row">
                                <div className="parties-empty-row__text">No passengers added. Click "Add Passenger" to add one.</div>
                            </div>
                        )}
                        {this.state.openModal === `AddPassengerModal_${party.pk}` && (
                            <AddPartyEntityModal
                                isOpen={true}
                                toggle={() => this.setState({ openModal: '' })}
                                entity="VEHICLE_PASSENGER"
                                partyId={party?.id}
                                refetchQuery={this.props.refetchQuery}
                            />
                        )}
                    </div>
                </div>
            </div>
        )
    }

    getVehicleHistoryIds = (party, filterKey) => {
        const showArchivedPeople = Boolean(this.state[`showArchivedPeople_${party.id}`])
        const peopleNodes = (party?.incidentpartypeopleSet?.edges || [])
            .filter(edge => Boolean(edge?.node?.isArchived) === showArchivedPeople)
            .map(edge => edge?.node)
            .filter(Boolean)
        const vehicleIds = (party?.vehicles?.edges || []).map(edge => edge?.node?.id).filter(Boolean)
        const driverIds = peopleNodes.filter(person => DRIVER_ROLES.includes(person.role)).map(person => person.id).filter(Boolean)
        const passengerIds = peopleNodes.filter(person => person.role === 'VEHICLE_PASSENGER').map(person => person.id).filter(Boolean)
        const ownerIds = peopleNodes.filter(person => person.role === OWNER_ROLE).map(person => person.id).filter(Boolean)

        const idsMap = {
            all: [...vehicleIds, ...driverIds, ...passengerIds, ...ownerIds],
            vehicle: vehicleIds,
            driver: driverIds,
            passenger: passengerIds,
            owner: ownerIds,
        }

        return Array.from(new Set(idsMap[filterKey] || []))
    }

    renderVehicleHistory = (party) => {
        const filterKey = this.state[`vehicleHistoryFilter_${party.id}`] || 'all'
        const setFilter = (option) => this.setState({ [`vehicleHistoryFilter_${party.id}`]: option?.value || 'all' })
        const historyObjectIds = this.getVehicleHistoryIds(party, filterKey)

        const options = [
            { value: 'all', label: 'All Updates' },
            { value: 'vehicle', label: 'Vehicle' },
            { value: 'driver', label: 'Driver' },
            { value: 'passenger', label: 'Passenger' },
            ...(!party.isDefault ? [{ value: 'owner', label: 'Owner' }] : []),
        ]

        return (
            <div className="parties-history">
                <div className="parties-history__controls">
                    <Select
                        value={options.find(option => option.value === filterKey)}
                        onChange={setFilter}
                        options={options}
                        isSearchable={false}
                    />
                </div>
                <div className="parties-history__content">
                    <HistoryList
                        objectIds={historyObjectIds}
                        emptyText="No history available"
                    />
                </div>
            </div>
        )
    }

    renderNonMotoristPartyInfo = (party) => {
        const showArchivedPeople = Boolean(this.state[`showArchivedPeople_${party.id}`])
        const hasIncidentDriver = Boolean(party?.incident?.driver?.id)
        const people = (party?.incidentpartypeopleSet?.edges || []).filter(edge => Boolean(edge?.node?.isArchived) === showArchivedPeople)
        return (
            <div className="parties-detail">
                <div className="parties-section">
                    <div className="parties-section__title parties-section__title--teal">Personal Information</div>
                    <div className="parties-section__content">
                        {people.map((person, idx) => (
                            <div key={person.node.id} className="parties-card parties-card--teal">
                                <div className="parties-card__header">
                                    <div className="parties-card__title">{toTitleCase(person.node.role || 'Party')} {idx + 1}</div>
                                </div>
                                <PersonInfo person={person.node} refetchQuery={this.props.refetchQuery} isDefault={party.isDefault} hasRental={Boolean(party?.incident?.rental?.id)} isReadOnly={showArchivedPeople || party.isArchived} hasDriver={hasIncidentDriver} branchCustomId={party?.incident?.branch?.customId} />
                            </div>
                        ))}
                        {people.length === 0 && (
                            <div className="parties-empty-row">
                                <div className="parties-empty-row__text">No party info added. Use "Add Party" to add one.</div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        )
    }

    renderPartyDetails = (party) => {
        const tabKey = `partyTab_${party.id}`
        const activeTab = this.state[tabKey] || (party.partyType === 'VEHICLE' ? 'seat' : 'party')
        const setTab = tab => this.setState({ [tabKey]: tab })
        const showArchivedPeople = Boolean(this.state[`showArchivedPeople_${party.id}`])
        const peopleEdges = (party?.incidentpartypeopleSet?.edges || []).filter(edge => Boolean(edge?.node?.isArchived) === showArchivedPeople)
        const filteredParty = { ...party, incidentpartypeopleSet: { edges: peopleEdges } }

        return (
            <div className="parties-panel">
                <div className="parties-panel__tabs">
                    {party.partyType === 'VEHICLE' && (
                        <button type="button" className={`parties-panel__tab ${activeTab === 'seat' ? 'is-active' : ''}`} onClick={() => setTab('seat')}>
                            <i className="fa fa-car" /> Seat Arrangement
                        </button>
                    )}
                    <button type="button" className={`parties-panel__tab ${activeTab === 'party' ? 'is-active' : ''}`} onClick={() => setTab('party')}>
                        <i className="fa fa-info-circle" /> Party Info
                    </button>
                    <button type="button" className={`parties-panel__tab ${activeTab === 'documents' ? 'is-active' : ''}`} onClick={() => setTab('documents')}>
                        <i className="fa fa-file-text-o" /> Documents
                    </button>
                    <button type="button" className={`parties-panel__tab ${activeTab === 'history' ? 'is-active' : ''}`} onClick={() => setTab('history')}>
                        <i className="fa fa-history" /> History
                    </button>
                </div>

                {party.partyType === 'VEHICLE' && activeTab === 'seat' && (
                    <SeatArrangement party={filteredParty} refetchQuery={this.props.refetchQuery} showArchivedPeople={showArchivedPeople} />
                )}
                {activeTab === 'party' && (party.partyType === 'VEHICLE' ? this.renderVehiclePartyInfo(party) : this.renderNonMotoristPartyInfo(party))}
                {activeTab === 'documents' && (
                    <ScopedDocumentListContainer id={this.props.id} slug={party?.documentSlug} category="PARTY" viewType="party" />
                )}
                {activeTab === 'history' && (
                    party.partyType === 'VEHICLE' ? this.renderVehicleHistory(party) : (
                        <HistoryList
                            objectIds={peopleEdges?.map(edge => edge?.node?.id).filter(Boolean) || []}
                            emptyText="No history available"
                        />
                    )
                )}
            </div>
        )
    }

    renderPartyRow = (party, index) => {
        const isOpen = this.state.openPartyId === party.id
        const isEditingName = this.state.editingPartyId === party.id
        const isVehicle = party.partyType === 'VEHICLE'
        const showArchivedPeople = Boolean(this.state[`showArchivedPeople_${party.id}`])
        const firstRole = party?.incidentpartypeopleSet?.edges?.find(edge => !edge?.node?.isArchived)?.node?.role || party?.incidentpartypeopleSet?.edges?.[0]?.node?.role
        const roleLabel = firstRole ? toTitleCase(firstRole) : '--'
        const titlePrefix = isVehicle ? 'Vehicle' : roleLabel
        const titleDetail = party.partyName || '--'
        return (
            <div key={party.id} className={"parties-row"}>
                <div className="parties-row__header" onClick={() => this.setState({ openPartyId: isOpen ? null : party.id })}>
                    <div className="parties-row__chevron">
                        <i className={`fa ${isOpen ? 'fa-chevron-down' : 'fa-chevron-right'}`} />
                    </div>
                    <div className="parties-row__info">
                        <div className="parties-row__title">
                            {"#" + party.pk} - {isEditingName ? (
                                <input
                                    type="text"
                                    className="parties-row__name-input"
                                    value={this.state.partyNameInput}
                                    onChange={(event) => this.setState({ partyNameInput: event.target.value, error: '' })}
                                    onClick={(event) => event.stopPropagation()}
                                />
                            ) : titleDetail}
                            <span className={`parties-row__badge ${!isVehicle && "non-vehicle"}`}>{titlePrefix}</span>
                            {party.isDefault && <span className="parties-row__badge">Default Party</span>}
                            {isVehicle && !party.isDefault && (party?.vehicles?.edges || []).some(edge => edge?.node?.belongsToFleet) && (
                                <>
                                    <span className="parties-row__badge parties-row__badge--fleet" id={`ourVehicleTag${party.pk}`}>VGM Vehicle</span>
                                    <UncontrolledTooltip target={`ourVehicleTag${party.pk}`}>This other vehicle's VIN matches a car in our fleet</UncontrolledTooltip>
                                </>
                            )}
                        </div>
                        <div className="parties-row__subtitle">Added <DatetimeRenderer datetime={party.dateAdded} /></div>
                    </div>
                    <div className="parties-row__actions">
                        {isOpen &&
                            <div className="parties-row__people-toggle" onClick={(event) => event.stopPropagation()}>
                                Show Archived
                                <i
                                    className={`fa fa-toggle-${showArchivedPeople ? "on" : "off"} archive-toggle-icon ${showArchivedPeople ? "toggle-green" : "toggle-red"}`}
                                    onClick={(event) => {
                                        event.stopPropagation()
                                        this.setState({ [`showArchivedPeople_${party.id}`]: !showArchivedPeople, openModal: '' })
                                    }}
                                />
                            </div>
                        }
                        {!party.isArchived &&
                            isEditingName ?
                            <>
                                <button type="button" className="parties-row__action" title="Save Party Name" onClick={(event) => { event.stopPropagation(); this.savePartyName() }} disabled={this.state.partyNameSaving}>
                                    <i className="fa fa-check" />
                                </button>
                                <button type="button" className="parties-row__action" title="Cancel Edit" onClick={(event) => { event.stopPropagation(); this.cancelPartyNameEdit() }} disabled={this.state.partyNameSaving}>
                                    <i className="fa fa-times" />
                                </button>
                            </>
                            : !party.isDefault &&
                            <button type="button" className="parties-row__action" title="Edit Party Name" onClick={(event) => { event.stopPropagation(); this.startPartyNameEdit(party) }}>
                                <i className="fa fa-pencil" />
                            </button>
                        }
                        {!party.isDefault && isOpen && (
                            <button
                                type="button"
                                className={`parties-row__action${party.isArchived ? ' parties-row__action--unarchive' : ' parties-row__action--archive'}`}
                                title={party.isArchived ? 'Un-Archive Party' : 'Archive Party'}
                                onClick={(event) => {
                                    event.stopPropagation()
                                    this.setState({ openModal: `archiveParty_${party.pk}` })
                                }}
                            >
                                {party.isArchived ? <i className="fa fa-undo" /> : <i className="fa fa-trash" />}
                            </button>
                        )}
                        {this.state.openModal === `archiveParty_${party.pk}` && this.renderArchivePartyModal(party.partyType, party.id)}
                    </div>
                </div>
                {isOpen && (
                    <div className="parties-row__body">
                        {this.renderPartyDetails(party)}
                    </div>
                )}
            </div>
        )
    }

    renderAddPartyMenu = () => {
        if (this.state.openModal !== 'AddPartyMenu') return null
        return (
            <div className="parties-add-menu">
                <button
                    type="button"
                    className="parties-add-menu__item"
                    onClick={() => this.setState({ openModal: 'AddVehicleModal' })}
                >
                    <i className="fa fa-car" />
                    Vehicle Party
                </button>
                <button
                    type="button"
                    className="parties-add-menu__item"
                    onClick={() => this.setState({ openModal: 'AddNonMotoristModal' })}
                >
                    <i className="fa fa-user" />
                    Non-Motorist Party
                </button>
            </div>
        )
    }

    render() {
        const { allIncidentParties, loading } = this.props
        const parties = allIncidentParties?.edges?.map(edge => edge.node) || []
        const vehicleParties = parties.filter(party => party.partyType === 'VEHICLE')
        const nonMotoristParties = parties.filter(party => ['NON_MOTORIST', 'CYCLIST', 'OTHER', 'WITNESS'].includes(party.partyType))
        const allParties = [...vehicleParties, ...nonMotoristParties]

        return (
            <div className="parties-management__content">
                {this.state.error && <Alert color="danger">{this.state.error}</Alert>}
                <div className="parties-toolbar">
                    <div className="parties-toolbar__left">
                        <div className="parties-toolbar__title">All Parties ({allParties.length})</div>
                        <Badge className="parties-toolbar__badge parties-toolbar__badge--blue">
                            <i className="fa fa-car" /> {vehicleParties.length} Vehicles
                        </Badge>
                        <Badge className="parties-toolbar__badge parties-toolbar__badge--teal">
                            <i className="fa fa-male" /> {nonMotoristParties.length} Non-Motorist
                        </Badge>
                    </div>
                    {!this.props.isArchived &&
                        <div className="parties-toolbar__right">
                            <Button color="primary" className="parties-toolbar__add" onClick={() => this.setState({ openModal: this.state.openModal === 'AddPartyMenu' ? '' : 'AddPartyMenu' })}>
                                <i className="fa fa-plus" /> Add Party <i className='fa fa-chevron-down' />
                            </Button>
                            {this.renderAddPartyMenu()}
                        </div>
                    }
                </div>

                {this.state.openModal === 'AddVehicleModal' && (
                    <AddVehicleModal
                        isOpen={true}
                        toggle={() => this.setState({ openModal: '' })}
                        incidentId={this.props?.id}
                        refetchQuery={this.props.refetchQuery}
                    />
                )}
                {this.state.openModal === 'AddNonMotoristModal' && (
                    <AddPeopleModal
                        isOpen={true}
                        toggle={() => this.setState({ openModal: '' })}
                        partyType="NON_MOTORIST"
                        roles={['WITNESS', 'CYCLIST', 'OTHER']}
                        incidentId={this.props?.id}
                        refetchQuery={this.props.refetchQuery}
                    />
                )}

                {(loading && !this.state.hasLoadedParties) ? (
                    <Skeleton count={5} height={64} />
                ) : (
                    <div className="parties-list">
                        {allParties?.map((party, index) => this.renderPartyRow(party, index))}
                        {allParties?.length === 0 && (
                            <div className="parties-empty">No parties found.</div>
                        )}
                    </div>
                )}
            </div>
        )
    }
}

export default graphql(AllIncidentParties, {
    options: ({ id, isArchived }) => ({
        variables: {
            incidentId: id,
            isArchived: isArchived
        },
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true
    }),
    props: ({ data: { allIncidentParties, loading, refetch, variables, } }) => ({
        allIncidentParties,
        loading,
        variables,
        refetchQuery: () => {
            return refetch({
                query: AllIncidentParties,
                variables: {
                    ...variables,
                },
                updateQuery: (previousResult, { fetchMoreResult }) => {
                    return { allIncidentParties: fetchMoreResult.allIncidentParties }
                },
            })
        },
    }),
})(withApolloClient(PartiesTab))
